/**
 * Crazy Cribbage Variant Manager
 * Encapsulates the rules, option definitions, zone mechanics (Bunkers, Peg-Through, Stink Hole),
 * special hand modifications (Zero Hand, Zero Crib, Rock the Crib, Stolen Crib),
 * and custom bonus evaluations for the Crazy Cribbage brand.
 */

export const CRAZY_OPTIONS_DEFS = Object.freeze([
  // Subgroup: General Options
  { key: 'jokers', chkId: 'optJokers', subgroup: 'general', type: 'boolean', name: 'Jokers', description: '2 wild cards per deck; represent any card in play, hands & crib', default: false },
  { key: 'optCutBeforeDiscard', chkId: 'optCutBeforeDiscard', subgroup: 'general', type: 'boolean', name: 'Cut before Discard', description: 'Pone cuts the starter card before players discard to the crib', default: false },
  { key: 'opt31sFor5', chkId: 'opt31sFor5', subgroup: 'general', type: 'boolean', name: "31's for 5", description: 'Combinations summing to 31 count for 5 bonus points in play, hand & crib', default: false },
  { key: 'opt19sFor2', chkId: 'opt19sFor2', subgroup: 'general', type: 'boolean', name: "19's for 2", description: 'Combinations summing to 19 count for 2 bonus points in play, hand & crib', default: false },
  { key: 'optRoundAroundTheWorld', chkId: 'optRoundAroundTheWorld', subgroup: 'general', type: 'boolean', name: 'Round around the World', description: 'Runs can wrap around Ace to King (e.g. K-A-2 or Q-K-A)', default: false },
  { key: 'optAllRed', chkId: 'optAllRed', subgroup: 'general', type: 'boolean', name: 'All Red', description: 'All-red cards in hand, crib, or pegging score 1 bonus point per card', default: false },
  { key: 'optAllBlack', chkId: 'optAllBlack', subgroup: 'general', type: 'boolean', name: 'All Black', description: 'All-black cards in hand, crib, or pegging score 1 bonus point per card', default: false },
  { key: 'optAllOdd', chkId: 'optAllOdd', subgroup: 'general', type: 'boolean', name: 'All Odd', description: 'All odd ranks (A, 3, 5, 7, 9, J, K) score 1 bonus point per card', default: false },
  { key: 'optAllEven', chkId: 'optAllEven', subgroup: 'general', type: 'boolean', name: 'All Even', description: 'All even ranks (2, 4, 6, 8, 10, Q) score 1 bonus point per card', default: false },
  { key: 'optAllDifferentSuits', chkId: 'optAllDifferentSuits', subgroup: 'general', type: 'boolean', name: 'All Different Suits', description: 'All cards having distinct suits score 1 bonus point per card', default: false },
  { key: 'optFlushInPlay', chkId: 'optFlushInPlay', subgroup: 'general', type: 'boolean', name: 'Flush in Play', description: '3 or more consecutive cards of the same suit in pegging score 1 pt per card', default: false },
  { key: 'optChutesAndLadders', chkId: 'optChutesAndLadders', subgroup: 'general', type: 'boolean', name: 'Chutes and Ladders', description: 'If at any time all players are tied, everyone goes back to the start (hole 0)', default: false },

  // Subgroup: Individual Cards
  { key: 'optKH', chkId: 'optKH', subgroup: 'cards', type: 'boolean', name: 'King of Hearts (Suicide King)', description: 'Scores 5 bonus points when in hand or crib (does not count as starter)', default: false },
  { key: 'optQS', chkId: 'optQS', subgroup: 'cards', type: 'boolean', name: 'Queen of Spades', description: 'Scores 5 bonus points when in hand or crib (does not count as starter)', default: false },
  { key: 'optAS', chkId: 'optAS', subgroup: 'cards', type: 'boolean', name: 'Ace of Spades', description: 'Scores 5 bonus points when in hand or crib (does not count as starter)', default: false },
  { key: 'optSpades', chkId: 'optSpades', subgroup: 'cards', type: 'boolean', name: 'Spades', description: 'Hands & cribs with a Spade double; no Spade loses 29 pts (crib immune if dealer holds a Spade; min 0). Cutting a Spade doubles pegging & heels', default: false },
  { key: 'optGuessStarter', chkId: 'optGuessStarter', subgroup: 'cards', type: 'boolean', name: 'Guess Starter', description: 'Pone guesses the rank of the cut starter card before it is turned over. A correct guess scores 3 points', default: false },

  // Subgroup: Poker Hands
  { key: 'optStraight', chkId: 'optStraight', subgroup: 'poker', type: 'boolean', name: 'Straight', description: 'A run of 5 or more cards scores 10 bonus points', default: false },
  { key: 'optFullHouse', chkId: 'optFullHouse', subgroup: 'poker', type: 'boolean', name: 'Full House', description: 'Three of a kind plus a pair in hand or crib scores 10 bonus points', default: false },
  { key: 'optStraightFlush', chkId: 'optStraightFlush', subgroup: 'poker', type: 'boolean', name: 'Straight Flush', description: 'Five consecutive cards of the same suit score 20 bonus points', default: false },
  { key: 'optRoyalFlush', chkId: 'optRoyalFlush', subgroup: 'poker', type: 'boolean', name: 'Royal Flush', description: '10-J-Q-K-A of the same suit scores 30 bonus points', default: false },

  // Subgroup: Pegging Options
  { key: 'optMultipleRunsInPegging', chkId: 'optMultipleRunsInPegging', subgroup: 'pegging', type: 'boolean', name: 'Multiple Runs in Pegging', description: 'Double runs, triple runs and quadruple runs can be scored during pegging', default: false },
  { key: 'optPegThroughZones', chkId: 'optPegThroughZones', subgroup: 'pegging', type: 'boolean', name: 'Peg Through Zones', description: "Holes 31–40, 61–70, 91–100 are peg-only zones; hand & crib points don't count", default: false, mutualGroup: 'zones' },
  { key: 'optLast5PegOut', chkId: 'optLast5PegOut', subgroup: 'pegging', type: 'boolean', name: 'Last 5 Peg Out', description: "Last 5 holes require pegging out; hand & crib points cannot cross finish", default: false, mutualGroup: 'zones' },
  { key: 'optBunkers', chkId: 'optBunkers', subgroup: 'pegging', type: 'boolean', name: 'Bunkers', description: 'First & last 5 holes, plus 50–60 & 80–90; pegging points only', default: false, mutualGroup: 'zones' },
  { key: 'optStinkHole', chkId: 'optStinkHole', subgroup: 'pegging', type: 'boolean', name: 'Stink Hole', description: 'At hole 120 (or 60 in quick game), you must score exactly 1 point to win', default: false, mutualGroup: 'zones' },
  { key: 'optProgressiveSteals', chkId: 'optProgressiveSteals', subgroup: 'pegging', type: 'boolean', name: 'Progressive Steals', description: "Pair opponent's card while pegging to steal it into your hand for counting", default: false },

  // Subgroup: Hands & Cribs
  { key: 'optStolenCrib', chkId: 'optStolenCrib', subgroup: 'hands', type: 'boolean', name: 'Stolen Crib', description: "Pone guesses dealer's exact crib points to steal all of them (2 players only)", default: false, requiredPlayers: 2 },
  { key: 'optZeroHand', chkId: 'optZeroHand', subgroup: 'hands', type: 'boolean', name: 'Zero Hand', description: 'A 0-point hand inflicts −19 penalty points', default: false },
  { key: 'optZeroCrib', chkId: 'optZeroCrib', subgroup: 'hands', type: 'boolean', name: 'Zero Crib', description: 'A 0-point crib inflicts −19 penalty points to dealer', default: false },
  { key: 'optRockCrib', chkId: 'optRockCrib', subgroup: 'hands', type: 'boolean', name: 'Rock the Crib', description: 'All crib points subtract from dealer score instead of adding', default: false },
  { key: 'optPickYourCrib', chkId: 'optPickYourCrib', subgroup: 'hands', type: 'boolean', name: 'Pick Your Crib', description: "Dealer discards normally, but steals card(s) from pone's hand for the crib", default: false },
  { key: 'optSpeedoCribbage', chkId: 'optSpeedoCribbage', subgroup: 'hands', type: 'boolean', name: 'Speedo Cribbage', description: "Pone discards to dealer's hand; dealer discards 4 (or 6 in super) to crib (2 players only)", default: false, requiredPlayers: 2 },
  { key: 'optShotgunCribbage', chkId: 'optShotgunCribbage', subgroup: 'hands', type: 'boolean', name: 'Shotgun Cribbage', description: 'Dealer forfeits all points scored in the crib', default: false },
  { key: 'optTwistedCribbage', chkId: 'optTwistedCribbage', subgroup: 'hands', type: 'boolean', name: 'Twisted Cribbage', description: 'Dealer combines 8 cards to make best two 4-card hands. Flushes require all 5 cards matching (2 players only)', default: false, requiredPlayers: 2 },
  { key: 'optFivesCribbage', chkId: 'optFivesCribbage', subgroup: 'hands', type: 'boolean', name: "Five's Cribbage", description: 'Players must discard any 5s in hand directly to the crib', default: false },
  { key: 'optSevensAndEightsCribbage', chkId: 'optSevensAndEightsCribbage', subgroup: 'hands', type: 'boolean', name: 'Sevens and Eights Cribbage', description: 'Players must discard any 7s and 8s in hand directly to the crib', default: false },
  { key: 'optDrawCribbage', chkId: 'optDrawCribbage', subgroup: 'hands', type: 'boolean', name: 'Draw Cribbage', description: 'Draw 0–5 cards after deal, discard 2 to crib, then discard & draw 0–3 cards (2 players only)', default: false, requiredPlayers: 2 },
  { key: 'opt7CardCribbage', chkId: 'opt7CardCribbage', subgroup: 'hands', type: 'boolean', name: '7 Card Cribbage', description: '7 cards dealt, 1 dealt to crib, both players discard 2 to crib (2 players only)', default: false, requiredPlayers: 2 },
  { key: 'optProgressiveCut', chkId: 'optProgressiveCut', subgroup: 'hands', type: 'boolean', name: 'Progressive cut', description: 'Cut 1 card for first 2 hands, 2 for next 2, etc. Game ends when >121 with equal cribs (2 players only)', default: false, requiredPlayers: 2 },
  { key: 'optCanadianDoubles', chkId: 'optCanadianDoubles', subgroup: 'hands', type: 'boolean', name: 'Canadian Doubles', description: '4-player partnership: dealer and pone get 10 cards, keep 4, pass 4 to partner, discard 2 to crib (forces 4 players partnership)', default: false, requiredPlayers: 4, requiredGameMode: 'partnership', requiredPlayerMode: '4_part' }
]);

export class CrazyManager {
  /**
   * @param {Object} [options]
   */
  constructor(options = {}) {
    this.options = { ...options };
    this.settings = options.settings || {};
  }

  /**
   * Updates internal settings reference.
   * @param {Object} settings
   */
  setSettings(settings = {}) {
    this.settings = settings;
  }

  /**
   * Retrieves definition for a Crazy option by its key.
   * @param {string} key
   * @returns {Object|null}
   */
  static getOptionDef(key) {
    return CRAZY_OPTIONS_DEFS.find(o => o.key === key || o.chkId === key) || null;
  }

  /**
   * Retrieves all options for a specific subgroup.
   * @param {string} subgroup
   * @returns {Array}
   */
  static getOptionsBySubgroup(subgroup) {
    return CRAZY_OPTIONS_DEFS.filter(o => o.subgroup === subgroup);
  }

  /**
   * Checks whether a hole is within a Peg Through Zone.
   * Hand and crib points cannot be pegged while inside this zone.
   * @param {number} hole
   * @returns {boolean}
   */
  isInPegThroughZone(hole) {
    return (hole >= 31 && hole <= 40) || (hole >= 61 && hole <= 70) || (hole >= 91 && hole <= 100);
  }

  /**
   * Checks whether a hole is in the Last 5 Peg Out zone.
   * Only pegging points can advance the peg out to victory.
   * @param {number} hole
   * @param {number} [target]
   * @returns {boolean}
   */
  isInLast5PegOut(hole, target = (this.settings.target || 121)) {
    const t = Number(target) || 121;
    return hole >= (t - 5) && hole < t;
  }

  /**
   * Checks whether a hole is in a Bunker zone.
   * In bunkers, only pegging points are scored.
   * @param {number} hole
   * @param {number} [target]
   * @returns {boolean}
   */
  isInBunker(hole, target = (this.settings.target || 121)) {
    const t = Number(target) || 121;
    if (hole >= 1 && hole <= 5) return true;
    if (hole >= (t - 5) && hole < t) return true;
    if (t === 121 || [241, 361, 481].includes(t)) {
      if (hole >= 50 && hole <= 60) return true;
      if (hole >= 80 && hole <= 90) return true;
    }
    return false;
  }

  /**
   * Checks whether a hole is the Stink Hole.
   * Must score exactly 1 point to win.
   * @param {number} hole
   * @param {number} [target]
   * @returns {boolean}
   */
  isInStinkHole(hole, target = (this.settings.target || 121)) {
    const t = Number(target) || 121;
    return hole === (t - 1);
  }

  /**
   * Evaluates hand score adjustments under Zero Hand rule.
   * If a player scores 0 points in their hand, they receive -19 points.
   * @param {number} rawPoints
   * @param {boolean} [isEnabled]
   * @returns {number}
   */
  evaluateZeroHand(rawPoints, isEnabled = this.settings.optZeroHand) {
    return (isEnabled && rawPoints === 0) ? -19 : rawPoints;
  }

  /**
   * Evaluates crib score adjustments under Zero Crib rule.
   * If the crib scores 0 points, the dealer receives -19 points.
   * @param {number} rawPoints
   * @param {boolean} [isEnabled]
   * @returns {number}
   */
  evaluateZeroCrib(rawPoints, isEnabled = this.settings.optZeroCrib) {
    return (isEnabled && rawPoints === 0) ? -19 : rawPoints;
  }

  /**
   * Evaluates Rock the Crib rule: crib points count as negative points.
   * @param {number} rawPoints
   * @param {boolean} [isEnabled]
   * @returns {number}
   */
  evaluateRockTheCrib(rawPoints, isEnabled = this.settings.optRockCrib) {
    return isEnabled ? -Math.abs(rawPoints) : rawPoints;
  }

  /**
   * Evaluates Guess Starter result.
   * A correct rank guess scores 3 points.
   * @param {number} guessedRank
   * @param {Array|Object} starter
   * @param {boolean} [isEnabled]
   * @returns {{ isHit: boolean, points: number }}
   */
  evaluateGuessStarter(guessedRank, starter, isEnabled = this.settings.optGuessStarter) {
    if (!isEnabled || !guessedRank || !starter) return { isHit: false, points: 0 };
    const starters = Array.isArray(starter) ? starter.filter(Boolean) : [starter];
    const isHit = starters.some(st => ((st.playAs && st.playAs.r) || st.rank || st.r || 0) === Number(guessedRank));
    return { isHit, points: isHit ? 3 : 0 };
  }

  /**
   * Resolves a Stolen Crib attempt.
   * If the pone correctly guesses the dealer's crib score, the pone steals those points.
   * Otherwise, the dealer keeps them.
   * @param {number} actualCribPoints
   * @param {number|null} poneGuess
   * @param {string} [dealerSeat]
   * @param {string} [poneSeat]
   * @returns {{ stolen: boolean, awardedTo: string, points: number }}
   */
  resolveStolenCrib(actualCribPoints, poneGuess, dealerSeat = 'p2', poneSeat = 'p1') {
    const stolen = (poneGuess != null && Number(poneGuess) === actualCribPoints);
    return {
      stolen,
      awardedTo: stolen ? poneSeat : dealerSeat,
      points: actualCribPoints
    };
  }

  /**
   * Evaluates individual card bonus points (King of Hearts, Queen of Spades, Ace of Spades).
   * @param {string} rank 'K', 'Q', 'A', etc.
   * @param {string} suit 'H', 'S', 'D', 'C'
   * @returns {number}
   */
  evaluateCardBonus(rank, suit) {
    if (this.settings.optKH && rank === 'K' && suit === 'H') return 5;
    if (this.settings.optQS && rank === 'Q' && suit === 'S') return 5;
    if (this.settings.optAS && rank === 'A' && suit === 'S') return 5;
    return 0;
  }

  /**
   * Evaluates a Muggins claim: if a player claimed fewer points than their actual score,
   * the uncounted difference can be stolen by their opponent.
   * @param {number} claimedPoints Points declared by showing player
   * @param {number} actualPoints Actual points earned by hand/crib
   * @param {string} claimantSeat The player claiming Muggins
   * @param {string} targetSeat The player who undercounted
   * @returns {{ valid: boolean, stolenPoints: number, claimant: string, target: string }}
   */
  evaluateMuggins(claimedPoints, actualPoints, claimantSeat, targetSeat) {
    const claimed = Number(claimedPoints) || 0;
    const actual = Number(actualPoints) || 0;
    const diff = actual - claimed;
    const valid = diff > 0;
    return {
      valid,
      stolenPoints: valid ? diff : 0,
      claimant: claimantSeat,
      target: targetSeat
    };
  }

  /**
   * Returns discard counts for Speedo Cribbage given dealer status and deal type.
   * Pone discards 2 cards (or 3 in super deal) to dealer's hand.
   * Dealer discards 4 cards (or 6 in super deal) to the crib.
   * @param {boolean} isDealer
   * @param {string} [dealType='std'] 'std' or 'super'
   * @returns {number}
   */
  getSpeedoDiscardCount(isDealer, dealType = 'std') {
    const isSuper = dealType === 'super';
    return isDealer ? (isSuper ? 6 : 4) : (isSuper ? 3 : 2);
  }

  /**
   * Evaluates Shotgun Cribbage: in Shotgun Cribbage, the dealer does not get to count
   * the points in the crib (crib scores 0 points).
   * @param {number} rawPoints
   * @param {boolean} [isEnabled=this.settings.optShotgunCribbage]
   * @returns {number}
   */
  evaluateShotgunCribbage(rawPoints, isEnabled = this.settings.optShotgunCribbage) {
    if (!isEnabled) return rawPoints;
    return 0;
  }

  /**
   * Validates whether discards satisfy Five's Cribbage rule: any 5s held in hand
   * must be discarded to the crib (up to discardN).
   * @param {Array} hand
   * @param {Array} discarded
   * @param {number} [discardN=2]
   * @param {boolean} [isEnabled=this.settings.optFivesCribbage]
   * @returns {{ valid: boolean, requiredFives: number, discardedFives: number }}
   */
  validateFivesCribbage(hand = [], discarded = [], discardN = 2, isEnabled = this.settings.optFivesCribbage) {
    if (!isEnabled) return { valid: true, requiredFives: 0, discardedFives: 0 };
    const rankOf = (c) => (c && (c.rank !== undefined ? c.rank : c.r)) || 0;
    const fivesInHand = hand.filter(c => rankOf(c) === 5);
    const requiredFives = Math.min(fivesInHand.length, discardN);
    const discardedFives = discarded.filter(c => rankOf(c) === 5).length;
    return {
      valid: discardedFives >= requiredFives,
      requiredFives,
      discardedFives
    };
  }

  /**
   * Validates whether discards satisfy Sevens and Eights Cribbage rule: any 7s and 8s held in hand
   * must be discarded to the crib (up to discardN).
   * @param {Array} hand
   * @param {Array} discarded
   * @param {number} [discardN=2]
   * @param {boolean} [isEnabled=this.settings.optSevensAndEightsCribbage]
   * @returns {{ valid: boolean, requiredCount: number, discardedCount: number }}
   */
  validateSevensAndEightsCribbage(hand = [], discarded = [], discardN = 2, isEnabled = this.settings.optSevensAndEightsCribbage) {
    if (!isEnabled) return { valid: true, requiredCount: 0, discardedCount: 0 };
    const rankOf = (c) => (c && (c.rank !== undefined ? c.rank : c.r)) || 0;
    const sevensAndEightsInHand = hand.filter(c => {
      const r = rankOf(c);
      return r === 7 || r === 8;
    });
    const requiredCount = Math.min(sevensAndEightsInHand.length, discardN);
    const discardedCount = discarded.filter(c => {
      const r = rankOf(c);
      return r === 7 || r === 8;
    }).length;
    return {
      valid: discardedCount >= requiredCount,
      requiredCount,
      discardedCount
    };
  }

  /**
   * Returns whether 7 Card Cribbage is active for the current settings.
   * Only enabled for 2 players.
   * @param {Object} [settings=this.settings]
   * @returns {boolean}
   */
  is7CardCribbage(settings = this.settings) {
    const pCount = Number(settings && settings.playerCount) || 2;
    return Boolean(settings && settings.opt7CardCribbage && pCount === 2);
  }

  /**
   * Returns whether Progressive Cut is active for the current settings.
   * Only enabled for 2 players.
   * @param {Object} [settings=this.settings]
   * @returns {boolean}
   */
  isProgressiveCut(settings = this.settings) {
    const pCount = Number(settings && settings.playerCount) || 2;
    return Boolean(settings && settings.optProgressiveCut && pCount === 2);
  }

  /**
   * Returns whether Progressive Steals is active for the current settings.
   * @param {Object} [settings=this.settings]
   * @returns {boolean}
   */
  isProgressiveSteals(settings = this.settings) {
    return Boolean(settings && settings.optProgressiveSteals);
  }

  /**
   * Returns whether Canadian Doubles is active for the current settings.
   * Only enabled for 4-player partnership games.
   * @param {Object} [settings=this.settings]
   * @returns {boolean}
   */
  isCanadianDoubles(settings = this.settings) {
    const pCount = Number(settings && settings.playerCount) || 2;
    return Boolean(settings && settings.optCanadianDoubles && pCount === 4);
  }

  /**
   * Returns an array of allowed player counts for a variant definition, or null if no restriction.
   * @param {Object} opt
   * @returns {number[]|null}
   */
  static getVariantRequiredPlayers(opt) {
    if (!opt || opt.requiredPlayers == null) return null;
    if (Array.isArray(opt.requiredPlayers)) return opt.requiredPlayers.map(Number);
    return [Number(opt.requiredPlayers)];
  }

  /**
   * Checks whether two variant definitions have compatible player count requirements.
   * Returns true if both can be played together or if either has no restriction.
   * @param {Object} optA
   * @param {Object} optB
   * @returns {boolean}
   */
  static areVariantPlayerRequirementsCompatible(optA, optB) {
    const reqA = CrazyManager.getVariantRequiredPlayers(optA);
    const reqB = CrazyManager.getVariantRequiredPlayers(optB);
    if (!reqA || !reqB) return true;
    const hasOverlap = reqA.some(count => reqB.includes(count));
    if (!hasOverlap) return false;
    if (optA.requiredGameMode && optB.requiredGameMode && optA.requiredGameMode !== optB.requiredGameMode) {
      return false;
    }
    if (optA.requiredPlayerMode && optB.requiredPlayerMode && optA.requiredPlayerMode !== optB.requiredPlayerMode) {
      return false;
    }
    return true;
  }

  /**
   * Returns whether a given player count and mode are allowed for a variant definition.
   * @param {Object} opt
   * @param {number} count
   * @param {string} [mode]
   * @param {string} [playerMode]
   * @returns {boolean}
   */
  static isPlayerCountAllowedForVariant(opt, count, mode, playerMode) {
    const req = CrazyManager.getVariantRequiredPlayers(opt);
    if (!req) return true;
    if (!req.includes(Number(count))) return false;
    if (opt.requiredGameMode && mode && opt.requiredGameMode !== mode) return false;
    if (opt.requiredPlayerMode && playerMode && opt.requiredPlayerMode !== playerMode) return false;
    return true;
  }
}

export function getVariantRequiredPlayers(opt) {
  return CrazyManager.getVariantRequiredPlayers(opt);
}

export function areVariantPlayerRequirementsCompatible(optA, optB) {
  return CrazyManager.areVariantPlayerRequirementsCompatible(optA, optB);
}

export function isPlayerCountAllowedForVariant(opt, count, mode, playerMode) {
  return CrazyManager.isPlayerCountAllowedForVariant(opt, count, mode, playerMode);
}

